import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Bot, Send, LifeBuoy, Plus, ChevronRight, Loader2 } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import StatusBadge from "../components/StatusBadge";
import { PageLoading, EmptyState } from "../components/States";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { fmtDateTime, setPageTitle } from "../lib/format";

const QUICK = ["How do bookings work?", "When do I get charged?", "How do host payouts work?", "How do I cancel a booking?"];
const CATEGORIES = ["booking", "payment", "listing", "account", "safety", "other"];

export default function Support() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState(null);
  const [convId, setConvId] = useState(null);
  const [history, setHistory] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "booking", subject: "", description: "" });
  const endRef = useRef(null);

  useEffect(() => {
    setPageTitle("Support");
    if (user) api.myTickets().then(setTickets).catch(() => setTickets([]));
    else setTickets([]);
  }, [user]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history]);

  const send = async (msg) => {
    const text = (msg ?? draft).trim();
    if (!text) return;
    setSending(true);
    setHistory((h) => [...h, { role: "user", text }]);
    setDraft("");
    try {
      const r = await api.aiChat(text, convId || undefined);
      setConvId(r.conversation_id);
      setHistory((h) => [...h, { role: "assistant", text: r.reply }]);
      if (r.escalated) toast.info("Escalated to a human agent — a ticket was created.");
    } catch (e) {
      setHistory((h) => [...h, { role: "assistant", text: "Sorry, I couldn't respond just now. Please create a ticket below and a human will help." }]);
      toast.error(e.message);
    } finally { setSending(false); }
  };

  const createTicket = async (e) => {
    e.preventDefault();
    try {
      await api.createTicket(form);
      toast.success("Ticket created — our team will reply soon");
      setShowForm(false);
      setForm({ category: "booking", subject: "", description: "" });
      setTickets(await api.myTickets());
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="support-page">
      <Navbar />
      <main className="flex-1 max-w-5xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Help center</p>
        <h1 className="type-display mt-3">Support</h1>
        <p className="mt-1 text-sm text-zinc-400">Ask our AI assistant, or open a ticket with the human team.</p>

        <div className="mt-8 grid lg:grid-cols-2 gap-6">
          {/* AI chat */}
          <div className="card-dark p-6 flex flex-col" data-testid="ai-support-chat">
            <div className="flex items-center gap-3 pb-4 border-b border-[#27272A]">
              <div className="w-9 h-9 rounded-full bg-[#E50914]/15 flex items-center justify-center"><Bot className="w-5 h-5 text-[#FF3B45]" /></div>
              <div>
                <p className="font-bold text-sm">MyRedPark Assistant</p>
                <p className="text-xs text-emerald-400">Online · replies instantly</p>
              </div>
              <span className="ml-auto badge-red">AI Powered</span>
            </div>
            <div className="flex-1 min-h-64 max-h-96 overflow-auto py-4 space-y-3" aria-live="polite">
              {history.length === 0 && (
                <div className="text-sm text-zinc-400">
                  <p>Hi! I can answer questions about bookings, payments, listings, and policies.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {QUICK.map((q) => (
                      <button key={q} onClick={() => send(q)} data-testid="ai-quick-prompt"
                              className="text-xs px-3 py-1.5 rounded-full border border-[#27272A] hover:border-[#E50914] transition-colors">{q}</button>
                    ))}
                  </div>
                </div>
              )}
              {history.map((m, i) => (
                <div key={i} className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "ml-auto bg-[#E50914] text-white" : "bg-[#080B0D] border border-[#27272A]"}`}>{m.text}</div>
              ))}
              {sending && <div className="bg-[#080B0D] border border-[#27272A] px-4 py-2.5 rounded-2xl text-sm w-16"><Loader2 className="w-4 h-4 animate-spin" /></div>}
              <div ref={endRef} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-4 flex gap-2">
              <input data-testid="ai-chat-input" value={draft} onChange={(e) => setDraft(e.target.value)}
                     className="input-dark flex-1" placeholder="Ask a question…" aria-label="Ask the AI assistant" />
              <button type="submit" data-testid="ai-chat-send" disabled={sending} className="btn-primary !px-4" aria-label="Send"><Send className="w-4 h-4" /></button>
            </form>
            <p className="mt-3 text-xs text-zinc-500">Sensitive issues (refunds, safety, account) are escalated to a human automatically.</p>
          </div>

          {/* Tickets */}
          <div className="space-y-6">
            <div className="card-dark p-6" data-testid="tickets-panel">
              <div className="flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2"><LifeBuoy className="w-4 h-4 text-[#FF3B45]" /> Your tickets</h2>
                {user && (
                  <button onClick={() => setShowForm((s) => !s)} data-testid="new-ticket-btn" className="btn-secondary !px-4 !py-2 text-xs" aria-expanded={showForm}>
                    <Plus className="w-4 h-4" /> New ticket
                  </button>
                )}
              </div>
              {!user ? (
                <p className="mt-5 text-sm text-zinc-400"><Link to="/login" className="text-[#FF3B45] hover:underline">Log in</Link> to view or create support tickets.</p>
              ) : showForm ? (
                <form onSubmit={createTicket} className="mt-5 space-y-4" data-testid="ticket-form">
                  <div>
                    <label htmlFor="t-cat" className="label-dark">Category</label>
                    <select id="t-cat" data-testid="ticket-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-dark">
                      {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="t-sub" className="label-dark">Subject</label>
                    <input id="t-sub" data-testid="ticket-subject" required maxLength={200} value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input-dark" placeholder="Short summary" />
                  </div>
                  <div>
                    <label htmlFor="t-desc" className="label-dark">Description</label>
                    <textarea id="t-desc" data-testid="ticket-description" required rows={4} maxLength={5000} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input-dark !h-auto py-3" placeholder="What happened? Include booking IDs if relevant." />
                  </div>
                  <button type="submit" data-testid="ticket-submit-btn" className="btn-primary w-full text-sm">Submit ticket</button>
                </form>
              ) : tickets === null ? <PageLoading label="Loading tickets…" /> : tickets.length === 0 ? (
                <EmptyState title="No tickets" body="If you ever need a human, your conversations will show up here." />
              ) : (
                <div className="mt-4 space-y-2.5" data-testid="tickets-list">
                  {tickets.map((t) => (
                    <Link key={t.ticket_id} to={`/support/tickets/${t.ticket_id}`} data-testid={`ticket-${t.ticket_id}`}
                          className="card-dark card-dark-hover p-4 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-sm truncate">{t.subject}</p>
                        <p className="text-xs text-zinc-500 mt-1">{t.category} · {fmtDateTime(t.created_at)}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <StatusBadge value={t.status} />
                        <ChevronRight className="w-4 h-4 text-zinc-500" />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
